import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen, MapPin, Clock, AlertTriangle, FlagOff, ChevronDown, ChevronUp,
  Camera, Users as UsersIcon, FileText, Plus, Truck, Mail, Image as ImageIcon,
  Images as ImagesIcon, Share2, ExternalLink,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Spinner, Button, Pill } from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import { OUTAGE_CARRIERS } from "./diaryHelpers";
// T-258
import ShareDiaryModal from "./ShareDiaryModal";
import { getDiaryPhotoSignedUrls } from "./diaryPhotoUpload";
import DiarySpaceModal from "./DiarySpaceModal";
// T-326 — client-side PDF + photo ZIP
import { downloadDiaryPdf } from "./diaryPdf";
import { downloadDiaryPhotosZip } from "./diaryZip";

// =============================================================================
// ProjectDiaryTab — embed on ProjectDetailScreen as a new tab.
// T-156. List of diary entries against this project, newest first. Click a
// row to expand inline and see the full detail + photo. Click "+ New entry"
// to drop into the field-app form (/capture/diary, pre-filtered to this
// project would be ideal — for v0 we just route to /capture/diary and the
// user picks the project).
// =============================================================================

interface DiaryPhoto {
  photo_id:   string;
  r2_key:     string;
  caption:    string | null;
  sort_order: number;
  photo_kind?: string | null;   // T-327 'general' | 'plant'
}

interface DiaryRow {
  diary_id:           string;
  project_id:         string;
  entry_date:         string;
  who_on_site:        any;
  started_at:         string | null;
  ended_at:           string | null;
  clock_in_method:    string | null;   // T-432 'clocked' | 'manual' | null
  clock_out_method:   string | null;
  conditions:         any;
  delays:             string | null;
  delay_billable:     boolean;
  delay_calc:         any;             // T-327 { reason, labour, plant, ta }
  scope_done:         string | null;
  outage:             any;
  project:            { project_code: string; project_name: string; clients: { legal_name: string | null } | null } | null;
  photo_storage_path: string | null;   // legacy single-photo; kept for backwards compat
  lat:                number | null;
  lng:                number | null;
  off_site:           boolean;
  off_site_distance_m: number | null;
  is_first_entry:     boolean;
  is_job_finished:    boolean;
  created_at:         string;
  created_by:         string | null;
  public_token:       string | null;    // T-326 client portal token
  // embedded
  creator: {
    user_id: string;
    employees: { first_name: string; last_name: string } | null;
  } | null;
  // T-258 — multi-photo array via nested select from site_diary_photos
  photos: DiaryPhoto[];
}

export default function ProjectDiaryTab({ projectId }: { projectId: string }) {
  const user = useRequireAuth();
  const canShare = hasRole(user, "manager");  // T-258 — manager+ only

  const [openId, setOpenId] = useState<string | null>(null);
  const [signedPhotoUrls, setSignedPhotoUrls] = useState<Record<string, string>>({});
  // T-258 — diary entry being shared (open ShareDiaryModal when set)
  const [shareDiaryId, setShareDiaryId] = useState<string | null>(null);
  // T-330 — project-level hosted client diary portal
  const [spaceOpen, setSpaceOpen] = useState(false);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["project_site_diary", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_diary_entries")
        .select(`
          diary_id, project_id, entry_date,
          who_on_site, started_at, ended_at, clock_in_method, clock_out_method, conditions,
          delays, delay_billable, delay_calc, scope_done, outage,
          photo_storage_path, lat, lng, off_site, off_site_distance_m,
          is_first_entry, is_job_finished, created_at, created_by, public_token,
          creator:created_by ( user_id, employees:employee_id (first_name, last_name) ),
          project:project_id ( project_code, project_name, clients ( legal_name ) ),
          photos:site_diary_photos ( photo_id, r2_key, caption, sort_order, photo_kind )
        `)
        .eq("project_id", projectId)
        .order("entry_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as DiaryRow[];
    },
  });

  // T-330 — the live client portal for this project (if any), so we can deep-
  // link to it from the header and flag which entries are already in it.
  const { data: activeShare } = useQuery({
    queryKey: ["diary_active_share", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_diary_shares")
        .select("token, expires_at, revoked_at, share_entries:site_diary_share_entries ( diary_id )")
        .eq("project_id", projectId)
        .is("revoked_at", null)
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      const sh = (data ?? [])[0] as any;
      if (!sh) return null;
      const live = !sh.expires_at || new Date(sh.expires_at).getTime() > Date.now();
      if (!live) return null;
      const daysLeft = sh.expires_at ? Math.ceil((new Date(sh.expires_at).getTime() - Date.now()) / 86400000) : null;
      return {
        url: `${window.location.origin}/diary-view/${sh.token}`,
        ids: new Set<string>((sh.share_entries ?? []).map((x: any) => x.diary_id)),
        daysLeft,
      };
    },
  });

  // T-258 — Lazy-load signed URLs for all photos of the OPEN row. Handles
  // both the legacy single photo_storage_path AND the new multi-photo array.
  // Uses batch fan-out via getDiaryPhotoSignedUrls — N parallel sign calls
  // are cheap at the CF edge. Cache by r2_key so re-opening the row doesn't
  // re-sign.
  useEffect(() => {
    if (!openId) return;
    const row = entries.find(e => e.diary_id === openId);
    if (!row) return;

    // Collect all R2 keys for this entry (legacy + multi-photo)
    const keysToSign: string[] = [];
    if (row.photo_storage_path && !signedPhotoUrls[row.photo_storage_path]) {
      keysToSign.push(row.photo_storage_path);
    }
    for (const ph of (row.photos ?? [])) {
      if (!signedPhotoUrls[ph.r2_key]) keysToSign.push(ph.r2_key);
    }
    if (keysToSign.length === 0) return;

    let cancelled = false;
    (async () => {
      const map = await getDiaryPhotoSignedUrls(keysToSign);
      if (cancelled || map.size === 0) return;
      setSignedPhotoUrls(prev => {
        const next = { ...prev };
        for (const [k, v] of map) next[k] = v;
        return next;
      });
    })();

    return () => { cancelled = true; };
  }, [openId, entries, signedPhotoUrls]);

  // Stats for the header
  const stats = useMemo(() => {
    const total = entries.length;
    const offSite = entries.filter(e => e.off_site).length;
    const billableDelays = entries.filter(e => e.delay_billable).length;
    return { total, offSite, billableDelays };
  }, [entries]);

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner size={20}/></div>;
  }

  return (
    <div className="space-y-3">
      {/* Header strip */}
      <Card>
        <div className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted">Site Diary</div>
            <div className="text-[18px] font-semibold mt-0.5">
              {stats.total} {stats.total === 1 ? "entry" : "entries"}
              {stats.offSite > 0   && <span className="ml-3 text-[12px] text-warn font-normal">· {stats.offSite} off-site</span>}
              {stats.billableDelays > 0 && <span className="ml-3 text-[12px] text-bid font-normal">· {stats.billableDelays} billable delay{stats.billableDelays===1?"":"s"}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canShare && activeShare && activeShare.daysLeft != null && activeShare.daysLeft <= 5 && (
              <button
                type="button"
                onClick={() => setSpaceOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-warn/10 border border-warn/40 text-warn text-[12px] font-medium hover:bg-warn/15"
              >
                <AlertTriangle size={13} />
                Portal expires in {activeShare.daysLeft} day{activeShare.daysLeft === 1 ? "" : "s"} — extend
              </button>
            )}
            {canShare && activeShare && (
              <Button variant="secondary" icon={ExternalLink} size="sm" onClick={() => window.open(activeShare.url, "_blank", "noopener")}>Open portal</Button>
            )}
            {canShare && entries.length > 0 && (
              <Button variant="secondary" icon={Share2} size="sm" onClick={() => setSpaceOpen(true)}>{activeShare ? "Manage portal" : "Client portal"}</Button>
            )}
            <Link to="/capture/diary">
              <Button variant="primary" icon={Plus} size="sm">New entry</Button>
            </Link>
          </div>
        </div>
      </Card>

      {entries.length === 0 ? (
        <Card>
          <div className="p-8 text-center text-text-muted">
            <BookOpen size={28} className="mx-auto mb-3 opacity-50"/>
            <div className="text-[14px] font-medium">No diary entries yet.</div>
            <div className="text-[12px] mt-1">
              Tap <span className="font-medium">New entry</span> to log today's work — who's on site, conditions, scope, delays, photo.
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {entries.map(e => (
            <DiaryRowCard
              key={e.diary_id}
              entry={e}
              expanded={openId === e.diary_id}
              onToggle={() => setOpenId(openId === e.diary_id ? null : e.diary_id)}
              legacyPhotoUrl={e.photo_storage_path ? signedPhotoUrls[e.photo_storage_path] : undefined}
              signedPhotoUrls={signedPhotoUrls}
              canShare={canShare}
              isShared={!!activeShare?.ids.has(e.diary_id)}
              onShare={() => setShareDiaryId(e.diary_id)}
            />
          ))}
        </div>
      )}

      {/* T-258 — per-entry share email */}
      {shareDiaryId && (
        <ShareDiaryModal
          diaryId={shareDiaryId}
          onClose={() => setShareDiaryId(null)}
        />
      )}

      {/* T-330 — hosted client diary portal (whole project space) */}
      {spaceOpen && (
        <DiarySpaceModal
          projectId={projectId}
          entries={entries.map(e => ({
            diary_id:   e.diary_id,
            entry_date: e.entry_date,
            scope_done: e.scope_done,
            photo_count: (e.photos?.length ?? 0) + (e.photo_storage_path ? 1 : 0),
            has_delay:  !!e.delays,
          }))}
          onClose={() => setSpaceOpen(false)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function DiaryRowCard({ entry, expanded, onToggle, legacyPhotoUrl, signedPhotoUrls, canShare, isShared, onShare }: {
  entry:           DiaryRow;
  expanded:        boolean;
  onToggle:        () => void;
  legacyPhotoUrl:  string | undefined;
  signedPhotoUrls: Record<string, string>;
  canShare:        boolean;
  isShared:        boolean;
  onShare:         () => void;
}) {
  const creator = entry.creator?.employees
    ? `${entry.creator.employees.first_name} ${entry.creator.employees.last_name}`
    : "Unknown";

  // Hours-on-site if both timestamps set
  const hours = useMemo(() => {
    if (!entry.started_at || !entry.ended_at) return null;
    const ms = new Date(entry.ended_at).getTime() - new Date(entry.started_at).getTime();
    if (ms <= 0) return null;
    return (ms / 1000 / 60 / 60).toFixed(1);
  }, [entry.started_at, entry.ended_at]);

  const crewCount     = entry.who_on_site?.crew?.length ?? 0;
  const visitorCount  = entry.who_on_site?.visitors?.length ?? 0;
  const weather       = entry.conditions?.weather;
  const carrierLabel  = entry.outage?.carrier
    ? OUTAGE_CARRIERS.find(c => c.value === entry.outage.carrier)?.label ?? entry.outage.carrier
    : null;

  // T-326 — client deliverable downloads (PDF + photo ZIP). Fetch fresh signed
  // URLs on click so they're valid regardless of what the gallery cached.
  const [busy, setBusy] = useState<null | "pdf" | "zip">(null);
  function fileBase() {
    const code = entry.project?.project_code ?? "site";
    return `${code.replace(/[^A-Za-z0-9]+/g, "_")}_${entry.entry_date}`;
  }
  async function loadPhotoUrls() {
    const photos = [...(entry.photos ?? [])].sort((a, b) => a.sort_order - b.sort_order);
    const keys = photos.map(p => p.r2_key);
    if (entry.photo_storage_path) keys.push(entry.photo_storage_path);
    const map = keys.length ? await getDiaryPhotoSignedUrls(keys, 600) : new Map<string, string>();
    return { photos, map };
  }
  async function handlePdf() {
    setBusy("pdf");
    try {
      const { photos, map } = await loadPhotoUrls();
      const pdfPhotos: { url: string; caption?: string | null; kind?: string | null }[] = [];
      if (entry.photo_storage_path && map.get(entry.photo_storage_path)) pdfPhotos.push({ url: map.get(entry.photo_storage_path)! });
      for (const p of photos) { const u = map.get(p.r2_key); if (u) pdfPhotos.push({ url: u, caption: p.caption, kind: p.photo_kind }); }
      await downloadDiaryPdf({
        projectCode: entry.project?.project_code ?? entry.project_id,
        projectName: entry.project?.project_name ?? "",
        clientName:  entry.project?.clients?.legal_name ?? null,
        entryDate:   entry.entry_date,
        scopeDone:   entry.scope_done,
        delays:      entry.delays,
        delayBillable: entry.delay_billable,
        delayCalc:   entry.delay_calc,
        conditions:  entry.conditions,
        whoOnSite:   entry.who_on_site,
        startedAt:   entry.started_at,
        endedAt:     entry.ended_at,
        outage:      entry.outage,
        photos:      pdfPhotos,
      });
    } catch (e: any) { alert(e?.message ?? "PDF failed"); }
    finally { setBusy(null); }
  }
  async function handleZip() {
    setBusy("zip");
    try {
      const { photos, map } = await loadPhotoUrls();
      const base = fileBase();
      const zipPhotos: { url: string; name: string }[] = [];
      if (entry.photo_storage_path && map.get(entry.photo_storage_path)) zipPhotos.push({ url: map.get(entry.photo_storage_path)!, name: `${base}_photo_0.jpg` });
      photos.forEach((p, i) => { const u = map.get(p.r2_key); if (u) zipPhotos.push({ url: u, name: `${base}_photo_${i + 1}.jpg` }); });
      if (zipPhotos.length === 0) { alert("No photos on this entry to download."); return; }
      const r = await downloadDiaryPhotosZip({ filenameBase: base, photos: zipPhotos });
      if (r.failed.length > 0) alert(`${r.zipped} of ${r.total} photos downloaded — ${r.failed.length} failed. Run it again for a complete set.`);
    } catch (e: any) { alert(e?.message === "none_fetched" ? "Couldn't fetch any photos — check your connection and try again." : (e?.message ?? "ZIP failed")); }
    finally { setBusy(null); }
  }

  return (
    <Card>
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-ink-raised/30 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {/* Top row — date, status pills */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[13px] text-accent">{fmtDate(entry.entry_date)}</span>
              {entry.is_first_entry && <Pill label="First entry" tone="bid" small/>}
              {entry.is_job_finished && <Pill label="Job finished" tone="ok" small/>}
              {isShared && <Pill label="In client portal" tone="live" small/>}
              {entry.off_site && <Pill label="Off-site" tone="critical" small/>}
              {entry.delay_billable && <Pill label="Billable delay" tone="bid" small/>}
            </div>

            {/* Scope summary line — first 100 chars */}
            {entry.scope_done && (
              <div className="text-[13px] text-text mt-1 line-clamp-2">{entry.scope_done}</div>
            )}

            {/* Metadata strip */}
            <div className="text-[11px] text-text-muted mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>By {creator}</span>
              {hours && <span className="inline-flex items-center gap-1"><Clock size={10}/> {hours}h on site</span>}
              {crewCount > 0 && <span className="inline-flex items-center gap-1"><UsersIcon size={10}/> {crewCount} crew{visitorCount > 0 ? ` + ${visitorCount} visitor${visitorCount===1?"":"s"}` : ""}</span>}
              {weather && Array.isArray(weather) && weather.length > 0 && <span>· {weather.join(", ")}</span>}
              {entry.conditions?.plant_on_site && <span className="inline-flex items-center gap-1"><Truck size={10}/> plant</span>}
              {carrierLabel && <span className="inline-flex items-center gap-1 text-warn"><AlertTriangle size={10}/> {carrierLabel} {entry.outage?.type ?? ""}</span>}
              {(entry.photo_storage_path || (entry.photos?.length ?? 0) > 0) && (
                <span className="inline-flex items-center gap-1">
                  <Camera size={10}/>
                  {(entry.photos?.length ?? 0) + (entry.photo_storage_path ? 1 : 0)} photo{((entry.photos?.length ?? 0) + (entry.photo_storage_path ? 1 : 0)) === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
          <div className="text-text-muted flex-shrink-0">
            {expanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
          </div>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-line/40 pt-3 space-y-3 text-[13px]">
          {/* T-258/T-326 — client deliverable actions (manager+) */}
          {canShare && (
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="secondary" size="sm" icon={FileText} disabled={busy !== null}
                onClick={(e) => { e.stopPropagation(); handlePdf(); }}>
                {busy === "pdf" ? "Building…" : "PDF report"}
              </Button>
              <Button variant="secondary" size="sm" icon={ImagesIcon} disabled={busy !== null}
                onClick={(e) => { e.stopPropagation(); handleZip(); }}>
                {busy === "zip" ? "Zipping…" : "Photos (ZIP)"}
              </Button>
              <Button variant="secondary" size="sm" icon={Mail}
                onClick={(e) => { e.stopPropagation(); onShare(); }}>
                Share with client
              </Button>
            </div>
          )}

          {/* T-258 — Multi-photo gallery (incl. legacy single photo if present) */}
          {((entry.photos?.length ?? 0) > 0 || entry.photo_storage_path) && (
            <div>
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted mb-2 inline-flex items-center gap-1">
                <ImageIcon size={10}/> Photos
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {/* Legacy single photo (if any) first */}
                {entry.photo_storage_path && (() => {
                  const url = legacyPhotoUrl;
                  return (
                    <a
                      key="legacy"
                      href={url ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block aspect-square bg-ink-raised border border-line rounded overflow-hidden hover:border-accent transition-colors"
                    >
                      {url ? (
                        <img src={url} alt="End-of-day site" className="w-full h-full object-cover"/>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Spinner size={14}/></div>
                      )}
                    </a>
                  );
                })()}
                {/* New multi-photo array, sorted */}
                {[...(entry.photos ?? [])]
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map(ph => {
                    const url = signedPhotoUrls[ph.r2_key];
                    return (
                      <a
                        key={ph.photo_id}
                        href={url ?? "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square bg-ink-raised border border-line rounded overflow-hidden hover:border-accent transition-colors relative group"
                        title={ph.caption ?? ""}
                      >
                        {url ? (
                          <img src={url} alt={ph.caption ?? "Site photo"} className="w-full h-full object-cover"/>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Spinner size={14}/></div>
                        )}
                        {ph.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-2 py-1 truncate md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                            {ph.caption}
                          </div>
                        )}
                      </a>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Time + GPS */}
          {(entry.started_at || entry.ended_at || entry.lat) && (
            <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-[11px] text-text-muted">
              {/* T-432 — a clocked time was stamped live on site; a manual one
                  was typed. Worth showing: it's the difference between a
                  record and a recollection when a client queries hours. */}
              {entry.started_at && (
                <span>Start: {new Date(entry.started_at).toLocaleString("en-AU")}
                  {entry.clock_in_method === "clocked" && <span className="text-ok ml-1">· clocked on</span>}
                </span>
              )}
              {entry.ended_at && (
                <span>End: {new Date(entry.ended_at).toLocaleString("en-AU")}
                  {entry.clock_out_method === "clocked" && <span className="text-ok ml-1">· clocked off</span>}
                </span>
              )}
              {entry.lat && entry.lng && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={10}/> {entry.lat.toFixed(5)}, {entry.lng.toFixed(5)}
                  {entry.off_site && entry.off_site_distance_m != null && (
                    <span className="text-warn ml-1">(off-site by {Math.round(entry.off_site_distance_m)}m)</span>
                  )}
                </span>
              )}
            </div>
          )}

          {/* Who on site — full list */}
          {(entry.who_on_site?.crew?.length || entry.who_on_site?.visitors?.length) ? (
            <div>
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted mb-1">Who was on site</div>
              <div className="flex flex-wrap gap-1.5">
                {(entry.who_on_site?.crew ?? []).map((c: any) => (
                  <span key={c.employee_id} className="px-2 py-0.5 bg-accent/10 border border-accent/30 rounded text-[11px]">
                    {c.name}
                    {c.mobile && <span className="text-text-muted ml-1 font-mono">· {c.mobile}</span>}
                  </span>
                ))}
                {(entry.who_on_site?.visitors ?? []).map((v: any, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-ink-raised border border-line rounded text-[11px]">
                    {v.name}
                    {v.company && <span className="text-text-muted"> · {v.company}</span>}
                    {v.phone   && <span className="text-text-muted font-mono"> · {v.phone}</span>}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* Conditions + plant */}
          {(entry.conditions?.weather?.length
            || entry.conditions?.temperature_c != null
            || entry.conditions?.wind_kmh != null
            || entry.conditions?.plant_on_site) && (
            <div>
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted mb-1">Conditions</div>
              <div className="text-[12px] space-y-0.5">
                {((entry.conditions.weather && Array.isArray(entry.conditions.weather) && entry.conditions.weather.length > 0) || entry.conditions.temperature_c != null || entry.conditions.wind_kmh != null) && (
                  <div>
                    {Array.isArray(entry.conditions.weather) ? entry.conditions.weather.join(", ") : null}
                    {entry.conditions.temperature_c != null && <> · {entry.conditions.temperature_c}°C</>}
                    {/* T-373 — wind, colour-banded (informational) */}
                    {entry.conditions.wind_kmh != null && (
                      <> · <span className={
                        entry.conditions.wind_kmh >= 46 ? "text-red-400"
                        : entry.conditions.wind_kmh >= 30 ? "text-amber-400"
                        : "text-emerald-400"
                      }>
                        {entry.conditions.wind_kmh} km/h ({entry.conditions.wind_kt ?? Math.round((entry.conditions.wind_kmh / 1.852) * 10) / 10} kt)
                      </span></>
                    )}
                  </div>
                )}
                {/* mig 378 — conditions.plant is a list; entries before it carry the single keys */}
                {entry.conditions.plant_on_site && (
                  Array.isArray(entry.conditions.plant) && entry.conditions.plant.length > 0
                    ? entry.conditions.plant
                    : [{ type: entry.conditions.plant_type, supplier: entry.conditions.plant_supplier, hours: entry.conditions.plant_hours, operator: entry.conditions.plant_operator }]
                ).map((p: any, i: number) => (
                  <div key={i} className="text-[12px]">
                    <div className="inline-flex items-center gap-1.5 font-medium">
                      <Truck size={11} className="text-text-muted"/>
                      {p.type || "Plant on site"}
                    </div>
                    <div className="text-text-muted text-[11px] ml-4 mt-0.5 space-x-2">
                      {p.supplier && <span>· {p.supplier}</span>}
                      {p.hours != null && p.hours !== "" && <span>· {p.hours}h on site</span>}
                      {p.operator && <span>· operator: {p.operator}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scope */}
          {entry.scope_done && (
            <div>
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted mb-1">Scope done</div>
              <div className="whitespace-pre-wrap">{entry.scope_done}</div>
            </div>
          )}

          {/* Delays */}
          {entry.delays && (
            <div>
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted mb-1 inline-flex items-center gap-1">
                <FlagOff size={10}/> Delays
                {entry.delay_billable && <span className="ml-1 text-bid">· billable</span>}
              </div>
              <div className="whitespace-pre-wrap">{entry.delays}</div>
            </div>
          )}

          {/* Outage */}
          {entry.outage && (
            <div>
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted mb-1 inline-flex items-center gap-1">
                <AlertTriangle size={10}/> Outage
              </div>
              <div className="text-[12px]">
                <span className="font-medium">
                  {OUTAGE_CARRIERS.find(c => c.value === entry.outage.carrier)?.label ?? entry.outage.carrier}
                </span>
                {entry.outage.type && <span> · {entry.outage.type}</span>}
                {entry.outage.ref && <span className="font-mono text-[11px] ml-1">{entry.outage.ref}</span>}
              </div>
              {(entry.outage.started_at || entry.outage.ended_at) && (
                <div className="text-[11px] text-text-muted mt-0.5">
                  {entry.outage.started_at && <>Off: {new Date(entry.outage.started_at).toLocaleString("en-AU")}</>}
                  {entry.outage.started_at && entry.outage.ended_at && " — "}
                  {entry.outage.ended_at && <>Back: {new Date(entry.outage.ended_at).toLocaleString("en-AU")}</>}
                </div>
              )}
              {entry.outage.notes && <div className="mt-1 text-[12px]">{entry.outage.notes}</div>}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
